/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ProductoEnCertificado from "./ProductoEnCertificado";
import {
  accessAPI,
  formatFecha,
  useEstetica,
  CardCertificadoAcreditado
} from "../../shared";
import logoVerticalBlanco from "../../images/logoVerticalBlanco.png";
import { InputText } from "primereact/inputtext";
import { Boton } from "../../shared";

import { Card } from "primereact/card";

export default function BusquedaCertificado() {
  const estetica = useEstetica();
  const [loader, setLoader] = useState(false);
  const [mensajeError, setMensajeError] = useState();
  const [certificadoABuscar, setCertificadoABuscar] = useState();
  const [certificadoEncontrado, setCertificadoEncontrado] = useState();
  const [numeroCertificado, setNumeroCertificado] = useState("");

  // Deeplinks a un certificado. Hay dos formas y las dos tienen que resolver.
  // `numeroEnUrl` para no chocar con el estado del campo de búsqueda.
  const {
    certificadoTipo,
    certificadoNumero,
    numeroCertificado: numeroEnUrl
  } = useParams();

  // /c/{numero}: el número impreso, tal cual. Es a donde apuntan los QR nuevos.
  useEffect(() => {
    if (numeroEnUrl) {
      setLoader(true);
      // Se escribe en el campo para que quede a la vista qué se está buscando,
      // incluso si no aparece: así se puede corregir a mano en vez de tener
      // que leer la URL.
      setNumeroCertificado(numeroEnUrl);
      setCertificadoABuscar({ ruta: encodeURIComponent(numeroEnUrl) });
    }
  }, [numeroEnUrl]);

  // /c/{letra}/{numero}: los QR ya impresos. La API rearma el número con la
  // fórmula de entonces (CP-{letra}-{numero}) y busca ese certificado.
  useEffect(() => {
    if (certificadoTipo && certificadoNumero) {
      setLoader(true);
      setCertificadoABuscar({ ruta: `${certificadoTipo}/${certificadoNumero}` });
    }
  }, [certificadoTipo, certificadoNumero]);

  // Cuando determina el certificado a buscar lo busca
  useEffect(() => {
    if (certificadoABuscar) {
      setMensajeError();
      setCertificadoEncontrado();
      setLoader(true);

      accessAPI(
        "GET",
        `certificado/detalles/${certificadoABuscar.ruta}`,
        null,
        (respuesta) => {
          setLoader(false);
          setCertificadoEncontrado(respuesta);
          // El número tal como está guardado. Normaliza lo que haya llegado
          // por la URL: un QR viejo entra como /c/j/1212 y el campo termina
          // mostrando CP-J-1212, que es lo que dice el certificado.
          const numero = respuesta?.certificado?.[0]?.numero;
          if (numero) setNumeroCertificado(numero);
        },
        (respuesta) => {
          setLoader(false);
          setMensajeError(respuesta[0]?.msg);
        }
      );
    }
  }, [certificadoABuscar]);

  // Establece los datos del certificado a buscar
  function buscarCertificado() {
    const buscado = numeroCertificado.trim();
    if (!buscado) {
      setMensajeError("Ingresá el número del certificado");
      return;
    }
    // Objeto y no string: buscar dos veces lo mismo tiene que volver a
    // consultar, y React no dispara el efecto si el valor es idéntico.
    setCertificadoABuscar({ ruta: encodeURIComponent(buscado) });
  }

  return (
    <main
      css={{
        paddingBottom: "50px",
        minHeight: "calc(100vh - var(--alturaFooter))",
        padding: "0px 10px"
      }}
    >
      <div
        css={{
          paddingTop: "50px",
          marginBottom: "30px",
          width: "100%",
          marginLeft: "auto",
          marginRight: "auto"
        }}
      >
        <img
          css={{
            width: "150px",
            marginLeft: "auto",
            marginRight: "auto",
            display: "block"
          }}
          // El logo en blanco del organismo; si no cargó uno, el de la
          // plataforma.
          src={estetica?.logos?.blanco || logoVerticalBlanco}
          alt="logo"
        />
      </div>

      <div css={{ paddingBottom: "50px" }}>
        <Card
          css={{
            maxWidth: "800px",
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
            marginBottom: "1em"
          }}
        >
          <h1>Buscador de certificados</h1>
          <p>
            Ingresá el número del certificado, tal como figura impreso, para ver
            los productos incluidos y la vigencia.
          </p>
          <div
            css={{
              fontSize: "2em",
              marginBottom: "1em",
              color: "var(--grisTextos)",
              fontWeight: "600",
              display: "flex",
              gap: "0.4em",
              justifyContent: "center"
            }}
          >
            <InputText
              value={numeroCertificado}
              onChange={(e) => setNumeroCertificado(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && buscarCertificado()}
              css={{ width: "100%", maxWidth: "22rem", textAlign: "center" }}
            />
          </div>
          <div>
            <Boton callback={buscarCertificado} loader={loader}>
              Buscar Certificado
            </Boton>
          </div>
        </Card>
        {mensajeError && (
          <div
            css={{
              marginLeft: "auto",
              marginRight: "auto",
              display: "block",
              color: "white",
              width: "fit-content",
              marginTop: "1em",
              fontWeight: "600",
              fontSize: "1.5em"
            }}
          >
            {mensajeError}
          </div>
        )}
        {certificadoEncontrado && (
          <Card
            css={{
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "center"
            }}
          >
            <div css={{ display: "flex", flexDirection: "column", gap: "1em" }}>
              <h1>Certificado {certificadoEncontrado.certificado?.[0]?.numero}</h1>
              <div>{certificadoEncontrado.certificadotipo.nombre}</div>
              <div>
                Emitido a nombre de{" "}
                <span css={{ fontWeight: "600" }}>
                  {certificadoEncontrado.cliente.razonsocial}
                </span>
              </div>
              <div>
                Norma:{" "}
                <span css={{ fontWeight: "600" }}>
                  {certificadoEncontrado.norma.numero}
                </span>
              </div>
              <div>
                Vencimiento:{" "}
                <span css={{ fontWeight: "600" }}>
                  {formatFecha(
                    certificadoEncontrado.certificado[0].fechavencimiento
                  )}
                </span>
              </div>
              <CardCertificadoAcreditado
                vinculos={certificadoEncontrado.norma.norma_x_acreditacion}
                sinCard
              />
              <div>Alcance:</div>
              {certificadoEncontrado.producto.map((producto, indexProducto) => {
                return (
                  <ProductoEnCertificado
                    producto={producto}
                    key={indexProducto}
                  />
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </main>
  );
}
